import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat';
import { provideRouter, withRouterConfig } from '@angular/router';

import { provideAuth } from '@ideals/core/auth';
import { provideClarity } from '@ideals/core/clarity';
import { provideFeatureFlags } from '@ideals/core/feature-flags';
import { provideGoogleAnalytics } from '@ideals/core/google-analytics';
import { provideLocalization } from '@ideals/core/localization';
import { provideLogger } from '@ideals/core/logger';
import { provideLogRocket } from '@ideals/core/logrocket';
import { providePlatform } from '@ideals/core/platform';
import { provideSentry } from '@ideals/core/sentry/sentry';
import { providePageTitleStrategy, provideRouteReuseStrategy, provideUrlSerializer } from '@ideals/core/strategies';
import { provideHelpCenterStore } from '@ideals/help-center/help-center';
import { provideTroubleshooting } from '@ideals/help-center/troubleshooting';
import { BreadcrumbsService } from '@ideals/services/breadcrumbs';
import { provideTheming } from '@ideals/theming';

import { baseHrefInterceptor } from './data/interceptors/base-href';
import { exhaustInterceptor } from './data/interceptors/exhaust';
import { handleErrorInterceptor } from './data/interceptors/handle-error';
import { normalizeErrorInterceptor } from './data/interceptors/normalize-error';
import { tokenRefreshInterceptor } from './data/interceptors/token-refresh';
import { LocalLoggerService } from './data/services/local-logger';
import { provideErrorHandler } from './data/strategies/error-handler';
import { toFailedRequest } from './data/utils/to-failed-request';
import { environment } from './environments/environment';

import { rootRoutes } from './root.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withXhr(),
      withInterceptors([
        baseHrefInterceptor,
        exhaustInterceptor,
        tokenRefreshInterceptor,
        handleErrorInterceptor,
        normalizeErrorInterceptor,
      ]),
      withInterceptorsFromDi(),
    ),
    provideAppInitializer(() => {
      const breadcrumbsService = inject(BreadcrumbsService);

      breadcrumbsService.initialize();
    }),
    provideAuth({
      authProviderUrl: environment.authProviderUrl,
      ignoreUrls: ['/api/v1/Watermark/content', '/api/v1/wopi/files'], // NOTE better way to do this?
    }),
    provideClarity(environment.clarityEnabled, environment.clarityKey),
    provideBrowserGlobalErrorListeners(),
    provideErrorHandler(),
    provideFeatureFlags({
      key: environment.featureFlagsKey,
      name: environment.featureFlagsName,
      url: environment.featureFlagsProviderUrl,
    }),
    provideLocalization(new URL(document.baseURI).origin),
    provideLogger([LocalLoggerService]),
    providePageTitleStrategy(),
    providePlatform(),
    provideRouter(rootRoutes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideRouteReuseStrategy(),
    provideSentry({
      dsn: environment.sentryDsn,
      enabled: environment.sentryEnabled,
      environment: environment.name,
      recognizeFailedRequest: toFailedRequest,
    }),
    provideSignalFormsConfig({ classes: NG_STATUS_CLASSES }),
    provideTheming(),
    provideTroubleshooting({
      fileStorageUrls: Object.values(environment.fileStorageProviderUrl),
      healthCheckUrls: environment.healthCheckUrls,
    }),
    provideUrlSerializer(),
    provideHelpCenterStore(environment.helpCenterUrl, environment.publicCloudFeaturesEnabled),
    provideGoogleAnalytics(environment.googleAnalytics),
    provideLogRocket(environment.logRocketEnabled, environment.logRocketAppId),
    provideZonelessChangeDetection(),
    {
      provide: APP_BASE_HREF,
      useFactory: () => window.location.origin,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
  ],
};
